import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuthSession, clientNoteApi, getMediaUrl } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import LiveCollaborativeSheet from '../components/LiveCollaborativeSheet';
import WorksheetFormModal from '../components/WorksheetFormModal';

export default function ClientWorksheetsPage() {
  const navigate = useNavigate();
  const { noteId } = useParams();
  const authSession = getAuthSession();

  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedFormNote, setSelectedFormNote] = useState(null);

  const fetchClientWorksheets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await clientNoteApi.getClientWorksheets();
      if (res?.worksheets) {
        setWorksheets(res.worksheets);
      }
    } catch (err) {
      console.error('Failed to load client worksheets:', err);
      setError(err.message || 'Failed to load assigned worksheets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authSession?.token) {
      navigate('/login');
      return;
    }
    fetchClientWorksheets();
  }, [authSession?.token, navigate, fetchClientWorksheets]);

  // If noteId is provided in URL params, open it directly or fetch it
  useEffect(() => {
    if (!noteId) return;

    if (worksheets.length > 0) {
      const target = worksheets.find(
        (w) => w._id === noteId || w.id === noteId || w.worksheet?.worksheetId?._id === noteId || w.worksheet?.worksheetId === noteId
      );
      if (target) {
        setSelectedFormNote(target);
        return;
      }
    }

    // Direct fetch fallback if opened via direct URL before or without full list
    if (!selectedFormNote) {
      clientNoteApi.getWorksheetNoteById(noteId)
        .then((res) => {
          if (res?.note) {
            setSelectedFormNote(res.note);
          }
        })
        .catch((err) => {
          console.warn('Could not auto-open worksheet by ID:', err.message);
        });
    }
  }, [noteId, worksheets, selectedFormNote]);

  // Filter worksheets based on search
  const filteredWorksheets = worksheets.filter((item) => {
    const ws = item.worksheet || {};
    const titleMatch = (ws.title || item.clientName || 'Worksheet')
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const therapistMatch = (item.physiotherapistId?.name || '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    return titleMatch || therapistMatch;
  });

  return (
    <>
      <DashboardSidebar userRole="client" />
      <div className="dashboard-page-content" style={{ marginLeft: '280px', padding: '30px', background: '#f8fafc', minHeight: '100vh' }}>
        
        {/* Top Header Banner */}
        <div
          style={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            borderRadius: '16px',
            padding: '28px 32px',
            color: '#ffffff',
            boxShadow: '0 10px 25px rgba(79, 70, 229, 0.2)',
            marginBottom: '30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px',
          }}
        >
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
              📑 Clinical Worksheets
            </div>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 800 }}>My Assigned Worksheets</h1>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', opacity: 0.9, maxWidth: '600px' }}>
              Interactive clinical worksheets assigned by your therapist. Changes and reflections you log are updated live in real time for both you and your therapist.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchClientWorksheets}
            style={{
              background: '#ffffff',
              color: '#4f46e5',
              padding: '10px 20px',
              borderRadius: '10px',
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          >
            🔄 Refresh Worksheets
          </button>
        </div>

        {/* Search Bar */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            border: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div style={{ flex: '1', minWidth: '260px' }}>
            <input
              type="text"
              placeholder="🔍 Search worksheets by title or therapist name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
            {filteredWorksheets.length} worksheet{filteredWorksheets.length !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* Error Notification */}
        {error && (
          <div style={{ padding: '14px 20px', background: '#fef2f2', color: '#b91c1c', borderRadius: '10px', marginBottom: '20px', border: '1px solid #fecaca' }}>
            ⚠️ {error}
          </div>
        )}

        {/* Worksheets Grid / Loading / Empty State */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>⏳</div>
            <p style={{ color: '#64748b', fontSize: '16px' }}>Loading your assigned worksheets...</p>
          </div>
        ) : filteredWorksheets.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: '22px',
            }}
          >
            {filteredWorksheets.map((item) => {
              const ws = item.worksheet || {};
              const wsDetails = ws.worksheetId || {};
              const therapist = item.physiotherapistId || {};
              const hasDynamicForm = Boolean(
                ws.hasDynamicForm ||
                (Array.isArray(ws.dynamicFields) && ws.dynamicFields.length > 0) ||
                wsDetails.hasDynamicForm ||
                (Array.isArray(wsDetails.dynamicFields) && wsDetails.dynamicFields.length > 0)
              );
              const isSubmitted = Boolean(ws.isFormSubmitted);

              return (
                <div
                  key={item._id}
                  style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    padding: '24px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                >
                  <div>
                    {/* Top Badges */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                      {hasDynamicForm ? (
                        <span
                          style={{
                            fontSize: '11.5px',
                            fontWeight: 700,
                            padding: '4px 10px',
                            borderRadius: '20px',
                            background: isSubmitted ? '#dcfce7' : '#fef3c7',
                            color: isSubmitted ? '#15803d' : '#b45309',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          {isSubmitted ? '🟢 Form Submitted' : '📝 Interactive Form To Fill'}
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: '11.5px',
                            fontWeight: 700,
                            padding: '4px 10px',
                            borderRadius: '20px',
                            background: '#dcfce7',
                            color: '#15803d',
                          }}
                        >
                          🟢 Live Interactive Sheet
                        </span>
                      )}

                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'Recent'}
                      </span>
                    </div>

                    {/* Worksheet Title */}
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                      {ws.title || 'Therapy Worksheet'}
                    </h3>

                    {/* Assigned By Therapist */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: '#ede9fe',
                          color: '#6d28d9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '13px',
                        }}
                      >
                        {therapist.name ? therapist.name.charAt(0).toUpperCase() : 'T'}
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                          {therapist.name ? `Dr. ${therapist.name}` : 'Your Therapist'}
                        </div>
                        <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                          {therapist.specializations?.[0] || 'Therapist'}
                        </div>
                      </div>
                    </div>

                    {/* Session Note / Instructions */}
                    {item.note && (
                      <div
                        style={{
                          background: '#f8fafc',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #f1f5f9',
                          fontSize: '13px',
                          color: '#475569',
                          lineHeight: '1.5',
                          marginBottom: '16px',
                        }}
                      >
                        <strong style={{ color: '#0f172a', display: 'block', marginBottom: '3px' }}>Session Note / Instructions:</strong>
                        <div style={{ whiteSpace: 'pre-wrap', maxHeight: '90px', overflowY: 'auto' }}>
                          {item.note}
                        </div>
                      </div>
                    )}

                    {/* Therapist Feedback Banner if available */}
                    {ws.therapistFeedback && (
                      <div
                        style={{
                          background: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '8px',
                          padding: '10px 12px',
                          fontSize: '12.5px',
                          color: '#166534',
                          marginBottom: '16px',
                        }}
                      >
                        <strong>💬 Therapist Feedback:</strong> {ws.therapistFeedback}
                      </div>
                    )}
                  </div>

                  {/* Actions Bar */}
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Dynamic Form Fill / View Button */}
                    {hasDynamicForm && (
                      <button
                        type="button"
                        onClick={() => setSelectedFormNote(item)}
                        style={{
                          width: '100%',
                          padding: '11px',
                          background: isSubmitted
                            ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
                            : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: '13.5px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          boxShadow: isSubmitted
                            ? '0 3px 8px rgba(2,132,199,0.3)'
                            : '0 4px 12px rgba(79,70,229,0.35)',
                        }}
                      >
                        {isSubmitted ? '👁️ View / Edit Submitted Form' : '✍️ Fill Worksheet Form'}
                      </button>
                    )}

                    {/* Live Sheet Open Button */}
                    <button
                      type="button"
                      onClick={() => setSelectedNote(item)}
                      style={{
                        width: '100%',
                        padding: '9px',
                        background: hasDynamicForm ? '#f8fafc' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: hasDynamicForm ? '#334155' : '#ffffff',
                        border: hasDynamicForm ? '1px solid #cbd5e1' : 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        boxShadow: hasDynamicForm ? 'none' : '0 3px 8px rgba(16,185,129,0.3)',
                      }}
                    >
                      📊 {hasDynamicForm ? 'Open Collaborative Grid' : 'Open & Edit Live Worksheet'}
                    </button>

                    {/* Download PDF/File if provided by Admin */}
                    {ws.fileUrl && (
                      <a
                        href={getMediaUrl(ws.fileUrl)}
                        download={ws.fileName || 'worksheet.pdf'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: '#f8fafc',
                          color: '#334155',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          fontWeight: 600,
                          fontSize: '12.5px',
                          textAlign: 'center',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                        }}
                      >
                        📥 Download Reference File
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '60px 20px',
              textAlign: 'center',
              border: '1px solid #e2e8f0',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>
              No Worksheets Assigned Yet
            </h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px', maxWidth: '480px', marginInline: 'auto' }}>
              When your therapist creates a session note and attaches a clinical worksheet or live reflection sheet, it will appear here so you can collaborate in real time.
            </p>
          </div>
        )}

        {/* LIVE COLLABORATIVE WORKSHEET MODAL */}
        {selectedNote && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.75)',
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              backdropFilter: 'blur(4px)',
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: '1050px',
                maxHeight: '90vh',
                overflowY: 'auto',
                borderRadius: '16px',
              }}
            >
              <LiveCollaborativeSheet
                noteId={selectedNote._id}
                initialNote={selectedNote}
                currentUserRole="client"
                onClose={() => {
                  setSelectedNote(null);
                  fetchClientWorksheets();
                }}
              />
            </div>
          </div>
        )}

        {/* INTERACTIVE WORKSHEET FORM MODAL */}
        {selectedFormNote && (
          <WorksheetFormModal
            note={selectedFormNote}
            isTherapist={false}
            onClose={() => {
              setSelectedFormNote(null);
              fetchClientWorksheets();
            }}
            onUpdated={(updatedNote) => {
              setSelectedFormNote(updatedNote);
              fetchClientWorksheets();
            }}
          />
        )}
      </div>
    </>
  );
}
